package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.XhrUpload
import dev.lingvo.site.api.putScreenshotCapture
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.files.Blob
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/** A selection is a stable batch, whether it came from a picker, drop or clipboard. */
data class ShotUploadSelection(val files: List<File>, val cell: ScreenshotsCell? = null, val device: DeviceClass? = null)

/** Each row retains its original replacement precondition while other uploads complete. */
class ShotUploadItem(val file: File, initial: ShotFilenameMatch, var expectedSha: String?) {
    var target: ShotFilenameMatch by mutableStateOf(initial)
    var phase: CaptureUploadPhase by mutableStateOf(CaptureUploadPhase.READY)
    var progress: Int by mutableStateOf(0)
    var message: String? by mutableStateOf(null)
    var warnings: List<String> by mutableStateOf(emptyList())
    var contentType: String = "image/png"
    val upload = XhrUpload()
    internal var job: Job? = null
}

/** Injectable at the real XHR boundary so tests can observe progress, cancellation and request admission. */
typealias ShotCaptureTransport = suspend (ShotUploadItem, (Long, Long) -> Unit) -> CaptureDto

/** Runs one file at a time and rechecks the original page authority before accepting a response. */
class ShotUploadBatch(
    val items: List<ShotUploadItem>, private val scope: CoroutineScope, private val authorized: () -> Boolean,
    private val transport: ShotCaptureTransport, private val onComplete: (CaptureDto) -> Unit,
    private val onRefusal: (ShotFilenameMatch, String) -> Unit = { _, _ -> },
) {
    private var batch: Job? = null
    var running: Boolean by mutableStateOf(false)
        private set

    /** Only failed rows are retried; successes are never uploaded twice. */
    fun start() {
        if (running || !authorized()) return
        running = true
        batch = scope.launch {
            try {
                items.filter { it.phase in setOf(CaptureUploadPhase.READY, CaptureUploadPhase.FAILED) }.forEach { item ->
                    if (item.phase !in setOf(CaptureUploadPhase.READY, CaptureUploadPhase.FAILED) || item.target.cell == null || item.target.device == null) return@forEach
                    val child = launch { send(item) }
                    item.job = child
                    child.join()
                }
            } finally { running = false }
        }
    }

    /** Cancellation retires the reader or XHR rather than merely hiding progress. */
    fun cancel(item: ShotUploadItem) {
        item.phase = CaptureUploadPhase.CANCELLED
        item.job?.cancel(); item.upload.abort()
    }

    /** Unmounting releases all network and file-reader work owned by the page. */
    fun close() { items.filter { it.phase in setOf(CaptureUploadPhase.READY, CaptureUploadPhase.CHECKING, CaptureUploadPhase.UPLOADING) }.forEach(::cancel); batch?.cancel() }

    private suspend fun send(item: ShotUploadItem) {
        try {
            if (!authorized()) return
            item.phase = CaptureUploadPhase.CHECKING; item.message = null; item.progress = 0
            val size = item.file.size.toLong()
            var verdict = if (size > ScreenshotSpecs.MAX_IMAGE_BYTES) precheckCapture(byteArrayOf(), size, item.target.device!!)
                else precheckCapture(readCaptureBytes(item.file.slice(0, 65536)), size, item.target.device!!)
            // The shared parser validates the entire container, including its end marker. A prefix
            // ending inside IDAT/SOS must be completed, bounded by the eight-MiB admission above.
            if (verdict == CapturePrecheck.NeedFullFile) verdict = precheckCapture(readCaptureBytes(item.file), size, item.target.device!!)
            if (verdict is CapturePrecheck.Refused) {
                item.phase = CaptureUploadPhase.FAILED; item.message = verdict.message
                onRefusal(item.target, verdict.message); return
            }
            item.warnings = (verdict as CapturePrecheck.Accepted).warnings
            item.contentType = verdict.header.format.mime
            if (!authorized()) { cancel(item); return }
            item.phase = CaptureUploadPhase.UPLOADING
            val result = transport(item) { sent, total ->
                if (authorized() && total > 0) item.progress = (sent * 100 / total).toInt().coerceIn(0, 100)
            }
            if (!authorized()) { cancel(item); return }
            item.phase = CaptureUploadPhase.DONE; item.progress = 100; onComplete(result)
        } catch (cancelled: CancellationException) {
            item.phase = CaptureUploadPhase.CANCELLED
            throw cancelled
        } catch (failure: Throwable) {
            if (authorized()) {
                item.phase = CaptureUploadPhase.FAILED; item.message = captureUploadFailure(failure)
                onRefusal(item.target, item.message!!)
            }
        }
    }
}

/** Reads browser bytes without base64, and aborts the reader when the file is cancelled. */
suspend fun readCaptureBytes(blob: Blob): ByteArray = suspendCancellableCoroutine { continuation ->
    val reader = FileReader()
    fun cleanup() { reader.onload = null; reader.onerror = null; reader.onabort = null }
    reader.onload = {
        val array = Int8Array(reader.result as ArrayBuffer)
        val bytes = ByteArray(array.length) { index -> array.asDynamic()[index] as Byte }
        cleanup(); if (continuation.isActive) continuation.resume(bytes)
    }
    reader.onerror = { cleanup(); if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("app.projects.slug.screenshots.capture_upload.couldnt_read_the_capture_file", "Couldn't read the capture file."))) }
    reader.onabort = { cleanup(); if (continuation.isActive) continuation.cancel() }
    continuation.invokeOnCancellation { cleanup(); reader.abort() }
    reader.readAsArrayBuffer(blob)
}

/** A native picker preserves keyboard access and the browser's folder-path metadata. */
@Composable
fun ShotCapturePicker(id: String, folder: Boolean = false, onFiles: (List<File>) -> Unit) {
    var refusal by remember { mutableStateOf<String?>(null) }
    Input(type = InputType.File, attrs = {
        id(id); attr("hidden", ""); attr("multiple", ""); attr("accept", "image/png,image/jpeg")
        if (folder) attr("webkitdirectory", "")
        onChange { event ->
            val files = event.target.files
            if (files != null) {
                refusal = captureBatchLimitMessage(files.length)
                if (refusal == null) onFiles((0 until files.length).mapNotNull { files.item(it) })
            }
            event.target.value = ""
        }
    })
    refusal?.let { LAlert(it, variant = LAlertVariant.WARNING) }
}

/** The mapping preview and progress share one batch across all three capture entry points. */
@Composable
fun CaptureUpload(
    slug: String, plan: ScreenshotPlanDto, coverage: CoverageDto, captures: List<CaptureDto>, selection: ShotUploadSelection,
    authorized: () -> Boolean, onClose: () -> Unit, onComplete: (CaptureDto) -> Unit,
    onRefusal: (ShotFilenameMatch, String) -> Unit = { _, _ -> }, transport: ShotCaptureTransport? = null,
) {
    val tooMany = captureBatchLimitMessage(selection.files.size)
    if (tooMany != null) {
        LModal(true, t("app.projects.slug.screenshots.capture_upload.capture_batch_is_too_large", "Capture batch is too large"), onClose) { LAlert(tooMany, variant = LAlertVariant.WARNING) }
        return
    }
    val scope = rememberCoroutineScope()
    val items = remember(selection) {
        val paths = selection.files.map { file -> ((file.asDynamic().lingvoRelativePath as? String) ?: (file.asDynamic().webkitRelativePath as? String))?.takeIf { it.isNotEmpty() } ?: file.name }
        val matches = matchShotFilenames(paths, plan, coverage.locales)
        selection.files.mapIndexed { index, file ->
            val target = if (selection.cell != null && selection.device != null && selection.files.size == 1)
                ShotFilenameMatch(paths[index], selection.cell, selection.device) else matches[index]
            ShotUploadItem(file, target, captures.firstOrNull { capture -> capture.slotId == target.cell?.slotId &&
                capture.locale == target.cell.locale && capture.deviceClass == target.device && capture.supersededAt == null }?.sha256)
        }
    }
    val batch = remember(selection) { ShotUploadBatch(items, scope, authorized, transport ?: { item, progress ->
        val target = item.target
        ApiClient.putScreenshotCapture(slug, plan.platform, target.cell!!.slotId, target.device!!, target.cell.locale,
            item.file, item.contentType, ifMatch = item.expectedSha,
            ifNoneMatch = if (item.expectedSha == null) "*" else null, upload = item.upload, onProgress = progress)
    }, onComplete, onRefusal) }
    DisposableEffect(batch) { onDispose { batch.close() } }
    val matched = items.count { it.target.cell != null && it.target.device != null }
    val failed = items.count { it.phase == CaptureUploadPhase.FAILED }
    LModal(true, tPlural("screenshots.upload.preview", matched, "{count} file matched, {unmatched} didn't", "{count} files matched, {unmatched} didn't",
        "unmatched" to items.size - matched), onClose = onClose) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LTaskProgress(items.count { it.phase == CaptureUploadPhase.DONE }, items.size,
                failed = failed + items.count { it.phase == CaptureUploadPhase.CANCELLED }, label = t("app.projects.slug.screenshots.capture_upload.capture_uploads", "Capture uploads"))
            LTable(caption = t("app.projects.slug.screenshots.capture_upload.capture_file_mapping", "Capture file mapping")) {
                LTableHead { LTableRow {
                    LTableHeaderCell { Text(t("app.projects.slug.screenshots.capture_upload.file", "File")) }
                    LTableHeaderCell { Text(t("app.projects.slug.screenshots.capture_upload.shot_language_and_device", "Shot, language and device")) }
                    LTableHeaderCell { Text(t("app.projects.slug.screenshots.capture_upload.status", "Status")) }
                } }
                LTableBody { items.forEach { item -> LTableRow {
                    LTableCell { Text(item.file.name) }
                    LTableCell {
                        if (item.phase == CaptureUploadPhase.READY) {
                            val targets = plan.slots.flatMap { slot -> coverage.locales.filter { slot.localizedUi || it.isSource }.flatMap { locale ->
                                slot.devices.map { device -> LSelectOption("${slot.id}:${locale.locale}:${device.id}", "${slot.title} · ${localeInfo(locale.locale).englishName} · ${screenshotDeviceLabel(device)}") }
                            } }
                            LSelect(value = item.target.let { if (it.cell == null) "" else "${it.cell.slotId}:${it.cell.locale}:${it.device?.id}" },
                                options = listOf(LSelectOption("", t("app.projects.slug.screenshots.capture_upload.choose_a_cell", "Choose a cell"))) + targets,
                                onValueChange = { raw ->
                                    val parts = raw.split(':')
                                    item.target = if (parts.size == 3) ShotFilenameMatch(item.target.path, ScreenshotsCell(parts[0], parts[1]), DeviceClass.fromId(parts[2]))
                                        else ShotFilenameMatch(item.target.path)
                                    item.expectedSha = captures.firstOrNull { capture -> capture.slotId == item.target.cell?.slotId &&
                                        capture.locale == item.target.cell?.locale && capture.deviceClass == item.target.device && capture.supersededAt == null }?.sha256
                                }, accessibleLabel = tf("screenshots.upload.assign_file", "Assign {file}", "file" to item.file.name))
                            item.target.reason?.let { LMuted(it) }
                        } else Text(item.target.cell?.let { "${it.locale} · ${item.target.device?.displayName}" }.orEmpty())
                    }
                    LTableCell {
                        Text(uploadPhaseLabel(item.phase, item.progress))
                        item.message?.let { LAlert(it, variant = LAlertVariant.WARNING, live = true) }
                        item.warnings.forEach { LAlert(it, variant = LAlertVariant.WARNING) }
                        if (item.phase in setOf(CaptureUploadPhase.CHECKING, CaptureUploadPhase.UPLOADING, CaptureUploadPhase.READY))
                            LButton(t("app.projects.slug.screenshots.capture_upload.cancel", "Cancel"), { batch.cancel(item) }, variant = LButtonVariant.GHOST)
                    }
                } } }
            }
            val duplicates = items.filter { it.phase != CaptureUploadPhase.CANCELLED && it.target.cell != null }
                .groupingBy { it.target.cell to it.target.device }.eachCount().any { it.value > 1 }
            if (duplicates) LAlert(t("app.projects.slug.screenshots.capture_upload.assign_each_file_to_a_different", "Assign each file to a different cell before uploading."), variant = LAlertVariant.WARNING)
            Row(Modifier.gap(Space.SM)) {
                LButton(if (failed > 0) tf("screenshots.upload.retry", "Retry the {count} that failed", "count" to failed)
                    else t("app.projects.slug.screenshots.capture_upload.upload_captures", "Upload captures"), { batch.start() },
                    enabled = authorized() && !batch.running && items.filter { it.phase != CaptureUploadPhase.CANCELLED }.all { it.target.cell != null && it.target.device != null } && !duplicates && items.any { it.phase in setOf(CaptureUploadPhase.READY, CaptureUploadPhase.FAILED) })
                LButton(t("app.projects.slug.screenshots.capture_upload.close", "Close"), onClose, variant = LButtonVariant.SECONDARY)
            }
        }
    }
}

private fun uploadPhaseLabel(phase: CaptureUploadPhase, progress: Int): String = when (phase) {
    CaptureUploadPhase.READY -> tr("app.projects.slug.screenshots.capture_upload.ready", "Ready")
    CaptureUploadPhase.CHECKING -> tr("app.projects.slug.screenshots.capture_upload.checking_image", "Checking image…")
    CaptureUploadPhase.UPLOADING -> trf("screenshots.upload.sent", "Uploading {percent}%", "percent" to progress)
    CaptureUploadPhase.DONE -> tr("app.projects.slug.screenshots.capture_upload.uploaded", "Uploaded")
    CaptureUploadPhase.FAILED -> tr("app.projects.slug.screenshots.capture_upload.failed", "Failed")
    CaptureUploadPhase.CANCELLED -> tr("app.projects.slug.screenshots.capture_upload.cancelled", "Cancelled")
}

/** Snapshots protected drag data during the event, then walks all directory-reader batches with bounds. */
fun collectDroppedCaptures(
    transfer: dynamic, scope: CoroutineScope, onFiles: (List<File>) -> Unit, onFailure: (String) -> Unit,
) {
    if (transfer == null) return
    val entries = mutableListOf<dynamic>()
    val fallback = mutableListOf<File>()
    val count = (transfer.items?.length as? Int) ?: 0
    captureBatchLimitMessage(count)?.let { onFailure(it); return }
    if (count > 0) repeat(count) { index ->
        val item = transfer.items[index]
        val entry = if (jsTypeOf(item.webkitGetAsEntry) == "function") item.webkitGetAsEntry() else null
        if (entry != null) entries.add(entry)
        else if (jsTypeOf(item.getAsFile) == "function") (item.getAsFile() as? File)?.let(fallback::add)
    } else {
        val length = (transfer.files?.length as? Int) ?: 0
        captureBatchLimitMessage(length)?.let { onFailure(it); return }
        repeat(length) { index -> fallback.add(transfer.files.item(index) as File) }
    }
    scope.launch {
        try {
            val files = fallback.toMutableList()
            var visited = 0
            suspend fun walk(entry: dynamic, parent: String, depth: Int) {
                check(depth <= 12 && ++visited <= MAX_CAPTURE_BATCH * 8) { tr("app.projects.slug.screenshots.capture_upload.this_folder_is_too_deep_or", "This folder is too deep or has too many entries. Choose its screenshots folder directly.") }
                val name = entry.name as String
                check(name.isNotBlank() && name !in setOf(".", "..") && '/' !in name && '\\' !in name && name.none { it.code < 32 }) {
                    tr("app.projects.slug.screenshots.capture_upload.this_folder_contains_an_unsafe_filename", "This folder contains an unsafe filename. Choose the original image files.")
                }
                val path = if (parent.isEmpty()) name else "$parent/$name"
                check(path.length <= 1024) { tr("app.projects.slug.screenshots.capture_upload.this_folder_path_is_too_long", "This folder path is too long. Choose its screenshots folder directly.") }
                if (entry.isFile == true) {
                    val file = suspendCancellableCoroutine<File> { continuation ->
                        entry.file({ value: File -> if (continuation.isActive) continuation.resume(value) },
                            { _: dynamic -> if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("app.projects.slug.screenshots.capture_upload.couldnt_read_one_of_the_capture", "Couldn't read one of the capture files. Try the file picker."))) })
                    }
                    captureBatchLimitMessage(files.size + 1)?.let { throw IllegalArgumentException(it) }
                    if (parent.isNotEmpty()) file.asDynamic().lingvoRelativePath = path
                    files.add(file)
                } else if (entry.isDirectory == true) {
                    val reader = entry.createReader()
                    while (true) {
                        val batch = suspendCancellableCoroutine<List<dynamic>> { continuation ->
                            reader.readEntries({ values: dynamic ->
                                if (continuation.isActive) continuation.resume((0 until (values.length as Int)).map { values[it] })
                            }, { _: dynamic -> if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("app.projects.slug.screenshots.capture_upload.couldnt_read_this_capture_folder_try", "Couldn't read this capture folder. Try the file picker."))) })
                        }
                        if (batch.isEmpty()) break
                        for (child in batch) walk(child, path, depth + 1)
                    }
                }
            }
            for (entry in entries) walk(entry, "", 0)
            if (files.isNotEmpty()) onFiles(files)
            else onFailure(tr("app.projects.slug.screenshots.capture_upload.no_files_were_found_in_this", "No files were found in this folder. Choose PNG or JPEG captures."))
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: Throwable) { onFailure(failure.message ?: tr("app.projects.slug.screenshots.capture_upload.couldnt_read_this_folder_try_the", "Couldn't read this folder. Try the file picker.")) }
    }
}
